import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  ModalController,
  PopoverController,
} from "ionic-angular";
import moment from "moment";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "period-options",
  templateUrl: "period-options.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodOptions),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PeriodOptions),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PeriodOptions implements OnInit, OnDestroy {
  @Input() tenantId: any;
  @Input() payerId: any;
  @Input() periods: any[] = [];
  @Input() currentPeriod: any;
  @Input() periodType: string = "";
  @Input() benefitType: string = "";
  @Input() benefitSubtype: string = "";
  @Input() localityCode: string = "";
  @Input() state: string = "";
  @Input() showButton: boolean = false;
  @Input() showEdit: boolean = false;
  @Input() showDelete: boolean = false;
  @Input() disabled: boolean = false;

  @Input() startName: string = "";
  @Input() endName: string = "";

  @Output() periodChanged = new EventEmitter<void>();

  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";

  minDateFormat: any;
  maxDateFormat: any;
  previousPeriod: any;
  showDeleteOption: boolean = false;

  request: any;

  constructor(
    private utilityService: UtilityService,
    private payersService: PayersService,
    private alertController: AlertController,
    private modalCtrl: ModalController,
    protected popoverCtrl: PopoverController
  ) {}

  ngOnInit() {}

  ngOnDestroy() {}

  logValues() {
    console.log("tenantId:", this.tenantId);
    console.log("payerId:", this.payerId);
    console.log("periods:", this.periods);
    console.log("currentPeriod:", this.currentPeriod);
    console.log("periodType:", this.periodType);
    console.log("benefitType:", this.benefitType);
    console.log("benefitSubType:", this.benefitSubtype);
    console.log("localityCode:", this.localityCode);
    console.log("state:", this.state);
    console.log("showButton:", this.showButton);
    console.log("showEdit:", this.showEdit);
    console.log("showDelete:", this.showDelete);
    console.log("showDeleteOption:", this.showDeleteOption);
    console.log("disabled:", this.disabled);
    console.log("minDateFormat:", this.minDateFormat);
    console.log("maxDateFormat:", this.maxDateFormat);
    console.log("previousPeriod:", this.previousPeriod);
    console.log("request:", this.request);
  }

  buttonDisabled() {
    if (this.periods && this.periods.length > 0) {
      this.periods.forEach((period, index) => {
        if (
          this.currentPeriod &&
          this.currentPeriod[this.startName] &&
          this.periods[index][this.startName] ==
            this.currentPeriod[this.startName]
        ) {
          this.currentPeriod = this.periods[index];
        }
        if (
          period[this.startName] &&
          moment(period[this.startName], this.dateUIFormat, true).isValid()
        ) {
          this.periods[index][this.startName] = moment(
            period[this.startName],
            this.dateUIFormat
          ).format(this.dateFormat);
        }
        if (
          period[this.endName] &&
          moment(period[this.endName], this.dateUIFormat, true).isValid()
        ) {
          this.periods[index][this.endName] = moment(
            period[this.endName],
            this.dateUIFormat
          ).format(this.dateFormat);
        }
      });
    }

    this.showDeleteOption =
      this.showDelete &&
      this.currentPeriod != null &&
      (this.currentPeriod[this.endName] == null ||
        this.currentPeriod[this.endName] === "");

    return (
      !(this.showDeleteOption || this.showEdit) ||
      this.disabled ||
      !this.currentPeriod ||
      !this.periods ||
      this.periods.length == 0 ||
      !this.periods.some(
        (period, index) => index > 0 && period.id === this.currentPeriod.id
      )
    );
  }

  showPeriodOptions(ev) {
    const options = this.popoverCtrl.create(
      "PeriodOptionsSelectPageComponent",
      {
        showEdit: this.showEdit,
        showDelete: this.showDeleteOption,
      },
      { cssClass: "payer-period-view-options-popover" }
    );
    options.onDidDismiss((data) => {
      if (data) {
        if (data === "edit") {
          this.openEditModal();
        } else if (data === "delete") {
          this.openDeleteModal();
        }
      }
    });
    options.present({ ev });
  }

  openEditModal() {
    this.setPeriodData();
    let title = "";
    if (this.periodType === "RATES") title = "Edit Contract Rates Period";
    else if (this.periodType === "BILLING_CODES")
      title = "Edit Contract Billing Codes Period";
    else if (this.periodType === "BILLING_OPTIONS")
      title = "Edit Contract Billing Options Period";
    else if (this.periodType === "REQUIREMENTS")
      title = "Edit Contract Requirements Period";
    let editContractPeriodModal = this.modalCtrl.create(
      "EditContractPeriodModalComponent",
      {
        minDateFormat: this.minDateFormat,
        maxDateFormat: this.maxDateFormat,
        currentPeriodStartDate: this.currentPeriod[this.startName],
        title: title,
      },
      { cssClass: "edit-contract-period-modal" }
    );
    editContractPeriodModal.onDidDismiss((data) => {
      if (data) {
        this.request = {
          tenantId: this.tenantId,
          payerId: this.payerId,
          periodType: this.periodType,
          benefitType: this.benefitType,
          benefitSubType: this.benefitSubtype,
          localityCode: this.localityCode,
          newStartDate: moment(data).format(this.dateFormat),
          period: {
            startDate: this.currentPeriod[this.startName],
            endDate: this.currentPeriod[this.endName],
          },
          previousPeriod: null,
        };
        if (this.previousPeriod) {
          this.request.previousPeriod = {
            startDate: this.previousPeriod[this.startName],
            endDate: this.previousPeriod[this.endName],
          };
        }
        this.showSavePayerModal();
      }
    });
    editContractPeriodModal.present();
  }

  showSavePayerModal() {
    let modalPage = this.modalCtrl.create(
      "SaveFacilityPage",
      {
        title: "Payer Information Change",
        commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
        popupMessageKey: "savePayerWarningMessage",
        section: "payer",
      },
      {
        enableBackdropDismiss: false,
        // NOVA-1908: payer confirm popup (section "payer") - use the payer
        // style hook so it is sized like the other payer tabs.
        cssClass: "save-payer-confirmation-popup",
      }
    );
    modalPage.onDidDismiss((data) => {
      if (data.save) {
        this.editPeriod();
      }
    });
    modalPage.present();
  }

  editPeriod() {
    this.utilityService.increaseSpinnerCount("Loading");
    this.payersService.updatePayerPeriod(this.request).subscribe(() => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        this.showAlert({
          title: "Success",
          message: "Contract period has been successfully updated.",
        });
        this.periodChanged.emit();
      }, 2000);
    }, this.onSavePeriodError);
  }

  private onSavePeriodError = (error) => {
    this.utilityService.decreaseSpinnerCount();
    console.log(error);
    if (error instanceof HttpErrorResponse) {
      if (error.error && error.error.message) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error,
        });
      } else if (error.message) {
        this.showAlert({
          title: "Error",
          message: "Error while processing request. " + error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while processing request. " + error,
        });
      }
    }
  };

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  setPeriodData() {
    this.minDateFormat = null;
    this.maxDateFormat = null;
    this.previousPeriod = null;
    this.request = null;

    let previousPeriodIndex: number = -1;
    this.periods.forEach((period, index) => {
      if (period.id === this.currentPeriod.id) {
        previousPeriodIndex = index + 1;
      }
      if (index === previousPeriodIndex) {
        this.previousPeriod = period;
        this.minDateFormat = moment(
          period[this.startName],
          this.dateFormat
        ).add(1, "day");
      }
    });
    if (this.currentPeriod[this.endName]) {
      this.maxDateFormat = moment(
        this.currentPeriod[this.endName],
        this.dateFormat
      );
    }
  }

  openDeleteModal() {
    this.setPeriodData();

    //Check if period is deletable
    this.payersService
      .countClaimsByContractPeriod(
        this.payerId,
        this.benefitType,
        this.currentPeriod[this.startName],
        this.currentPeriod[this.endName],
        this.benefitSubtype,
        this.state
      )
      .subscribe((countAffectedClaims: number) => {
        let periodDeletable: boolean = countAffectedClaims === 0;

        if (periodDeletable) {
          let modalData = {};
          if (this.periodType === "RATES")
            modalData = {
              title: "Delete Contract Rates Period",
              line1:
                "This action will delete the Contract Rates Period for this payer.",
              line2: "Are you sure you would like to proceed?",
            };
          else if (this.periodType === "BILLING_CODES")
            modalData = {
              title: "Delete Contract Billing Codes Period",
              line1:
                "This action will delete the Contract Billing Codes Period for this payer.",
              line2: "Are you sure you would like to proceed?",
            };
          else if (this.periodType === "BILLING_OPTIONS")
            modalData = {
              title: "Delete Contract Billing Options Period",
              line1:
                "This action will delete the Contract Billing Options Period for this payer.",
              line2: "Are you sure you would like to proceed?",
            };
          else if (this.periodType === "REQUIREMENTS")
            modalData = {
              title: "Delete Contract Requirements Period",
              line1:
                "This action will delete the Contract Requirements Period for this payer.",
              line2: "Are you sure you would like to proceed?",
            };

          let deleteContractPeriodModal = this.modalCtrl.create(
            "DeleteContractPeriodModalComponent",
            modalData,
            { cssClass: "delete-contract-period-modal" }
          );
          deleteContractPeriodModal.onDidDismiss((data) => {
            if (data) {
              if (this.periodType === "RATES") this.deleteRatesPeriod();
              if (this.periodType === "BILLING_CODES")
                this.deleteBillingCodesPeriod();
              if (this.periodType === "BILLING_OPTIONS")
                this.deleteBillingOptionsPeriod();
              if (this.periodType === "REQUIREMENTS")
                this.deleteRequirementsPeriod();

              this.periodChanged.emit();
            }
          });
          deleteContractPeriodModal.present();
        } else {
          let modalData = {};
          if (this.periodType === "RATES")
            modalData = {
              title: "Delete Contract Rate Period",
              line1:
                "This Contract Rate Period cannot be deleted because there are existing claims associated.",
            };
          else if (this.periodType === "BILLING_CODES")
            modalData = {
              title: "Delete Contract Billing Code Period",
              line1:
                "This Contract Billing Code Period cannot be deleted because there are existing claims associated.",
            };
          else if (this.periodType === "BILLING_OPTIONS")
            modalData = {
              title: "Delete Contract Billing Options Period",
              line1:
                "This Contract Billing Options Period cannot be deleted because there are existing claims associated.",
            };
          else if (this.periodType === "REQUIREMENTS")
            modalData = {
              title: "Delete Contract Requirements Period",
              line1:
                "This Contract Requirements Period cannot be deleted because there are existing claims associated.",
            };
          let noDeleteContractPeriodModal = this.modalCtrl.create(
            "NoDeleteContractPeriodModalComponent",
            modalData,
            { cssClass: "no-delete-contract-period-modal" }
          );
          noDeleteContractPeriodModal.present();
        }
      });
  }

  deletePeriod(apiPrefix) {
    this.utilityService.increaseSpinnerCount("Loading");
    this.payersService
      .deletePayerPeriod(
        this.payerId,
        this.benefitType,
        this.benefitSubtype,
        this.localityCode,
        this.currentPeriod[this.startName],
        apiPrefix
      )
      .subscribe(() => {
        setTimeout(() => {
          this.utilityService.decreaseSpinnerCount();
          this.showAlert({
            title: "Success",
            message: "Contract period has been successfully deleted.",
          });
          this.periodChanged.emit();
        }, 2000);
      }, this.onSavePeriodError);
  }

  deleteRatesPeriod() {
    const apiPrefix = "payer-rates";
    this.deletePeriod(apiPrefix);
  }

  deleteBillingCodesPeriod() {
    const apiPrefix = "service-codes";
    this.deletePeriod(apiPrefix);
  }

  deleteBillingOptionsPeriod() {
    const apiPrefix = "payer-billing-options";
    this.deletePeriod(apiPrefix);
  }

  deleteRequirementsPeriod() {
    const apiPrefix = "payer-requirements";
    this.deletePeriod(apiPrefix);
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/period-options/period-options.ts